import './styles.scss';

import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import DataTable from 'examples/Tables/DataTable';
import {Confirm, Loading, Notify} from 'notiflix';
import {Axios} from 'services/axios';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {
    Header: 'SubCategory Name',
    accessor: 'subCategoryName',
    width: '20%',
    align: 'left',
  },
  {
    Header: 'Description',
    accessor: 'description',
    width: '20%',
    align: 'center',
  },
  {
    Header: 'Selection Type',
    accessor: 'selectionType',
    width: '20%',
    align: 'center',
  },
  {Header: 'Status', accessor: 'status', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'center'},
];

// Build table row elements for each subcategory
const getRow = (sub, idx, handleEdit, handleDelete) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  subCategoryName: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {sub.subCategoryName}
    </MDTypography>
  ),
  description: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {sub.description}
    </MDTypography>
  ),
  selectionType: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {sub.selectionType}
    </MDTypography>
  ),
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={sub.active ? 'Active' : 'Inactive'}
        color={sub.active ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  action: (
    <MDBox ml={-1} display="flex" gap={1}>
      <MDBadge
        badgeContent="edit"
        color="info"
        variant="gradient"
        size="sm"
        onClick={() => handleEdit(sub.id)}
        style={{cursor: 'pointer'}}
      />
      <MDBadge
        badgeContent="Delete"
        color="error"
        variant="gradient"
        size="sm"
        onClick={() => handleDelete(sub.id)}
        style={{cursor: 'pointer'}}
      />
    </MDBox>
  ),
});

const AdditionalFeatureCategoriesTable = ({
  subCategories,
  setSubCategories,
  categoryId,
  onEdit,
  onDelete,
}) => {
  // State for managing the edit dialog and form data
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [editForm, setEditForm] = useState({
    id: '',
    categoryId: '',
    subCategoryName: '',
    description: '',
    selectionType: '',
    active: false,
  });

  // When a subcategory is selected, update the edit form.
  useEffect(() => {
    if (selectedSubCategory) {
      setEditForm({
        id: selectedSubCategory.id,
        // Always use the passed-in categoryId so that it is sent with the update payload.
        categoryId: categoryId,
        subCategoryName: selectedSubCategory.subCategoryName,
        description: selectedSubCategory.description,
        selectionType: selectedSubCategory.selectionType,
        active: selectedSubCategory.active,
      });
    }
  }, [selectedSubCategory, categoryId]);

  // Open the internal edit dialog if no external onEdit callback is provided.
  const handleEditSubCategory = id => {
    if (onEdit) {
      onEdit(id);
    } else {
      const sub = subCategories.find(s => s.id === id);
      if (sub) {
        setSelectedSubCategory(sub);
        setOpenEditDialog(true);
      }
    }
  };

  // Delete callback (can be overridden via onDelete prop)
  const handleDeleteSubCategory = id => {
    if (onDelete) {
      onDelete(id);
    } else {
      Confirm.show(
        'Delete SubCategory?',
        'Do you agree to delete this subcategory?',
        'Yes',
        'No',
        async () => {
          try {
            Loading.circle('Deleting sub category');
            const sub = subCategories.find(s => s.id === id);
            await Axios.delete(
              `/shop/subCategories/${id}?label=${encodeURIComponent(sub?.subCategoryName || '')}`,
            );
            Notify.success('SubCategory deleted successfully');
            // Update the parent's subCategories state immediately.
            setSubCategories(prev => prev.filter(s => s.id !== id));
          } catch (err) {
            console.error(err);
            Notify.failure(
              err.response?.data?.message || 'Failed to delete subcategory',
            );
          } finally {
            Loading.remove('Deleting sub category');
          }
        },
        () => {},
        {
          width: '320px',
          borderRadius: '8px',
          backgroundColor: '#fff',
        },
      );
    }
  };

  // Handle updating a subcategory.
  // The payload now includes the parent categoryId.
  const handleUpdateSubCategory = async e => {
    Loading.circle('Updating sub category');

    e.preventDefault();

    const payload = {
      id: selectedSubCategory.id,
      categoryId: categoryId,
      subCategoryName: editForm.subCategoryName,
      description: editForm.description,
      selectionType: editForm.selectionType,
      active: editForm.active,
    };

    try {
      const response = await Axios.post(
        '/shop/addUpdateSubCategoryForAdditionalFeatures',
        payload,
      );
      Notify.success(
        response.data.message || 'SubCategory updated successfully',
      );
      setOpenEditDialog(false);
      setSelectedSubCategory(null);

      // Update the parent's subCategories state immediately.
      setSubCategories(prev =>
        prev.map(s => (s.id === payload.id ? {...s, ...payload} : s)),
      );
    } catch (err) {
      console.error(err);
      Notify.failure(
        err.response?.data?.message || 'Failed to update subcategory',
      );
    } finally {
      Loading.remove('Updating sub category');
    }
  };

  return (
    <>
      <MDBox mt={3} className="additionalFeatureCategoryTable">
        <DataTable
          table={{
            columns,
            rows: (subCategories || []).map((sub, idx) =>
              getRow(sub, idx, handleEditSubCategory, handleDeleteSubCategory),
            ),
          }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
          hoverEffect
        />
      </MDBox>

      {/* Edit SubCategory Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        className="addUpdateAdditionalFeatureCategory">
        <DialogTitle>Edit Sub Category</DialogTitle>
        <DialogContent>
          <form onSubmit={handleUpdateSubCategory}>
            <TextField
              label="SubCategory Name"
              fullWidth
              margin="normal"
              value={editForm.subCategoryName}
              onChange={e =>
                setEditForm({...editForm, subCategoryName: e.target.value})
              }
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              value={editForm.description}
              onChange={e =>
                setEditForm({...editForm, description: e.target.value})
              }
            />
            <TextField
              label="Selection Type"
              fullWidth
              margin="normal"
              select
              value={editForm.selectionType}
              onChange={e =>
                setEditForm({...editForm, selectionType: e.target.value})
              }>
              <MenuItem value="radio">Radio</MenuItem>
              <MenuItem value="checkbox">Checkbox</MenuItem>
              <MenuItem value="range">Range</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.active}
                  onChange={e =>
                    setEditForm({...editForm, active: e.target.checked})
                  }
                />
              }
              label="Active"
            />
            <DialogActions>
              <MDButton
                onClick={() => setOpenEditDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                Update
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdditionalFeatureCategoriesTable;
